import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import {
  applyAppleTouchIcon,
  applyPageManifest,
  type PageManifestIcon,
  type PageManifestIdentity,
} from '@/lib/page-manifest';

/**
 * PageManifestLink — points the document's manifest at the page you are on,
 * and names it after whatever that page actually is.
 *
 * Mounted INSIDE <BrowserRouter /> so it can call useLocation(), the same
 * arrangement as RouteTracker. Renders nothing.
 *
 * WHY this exists rather than a fixed `start_url` in the static manifest: iOS
 * and Chrome want opposite things from one file. iOS takes "Add to Home
 * Screen" from `start_url` once a manifest exists, so a fixed `"/"` meant
 * every icon opened the Fias root and Flappyland could not be put on a home
 * screen. Dropping the field fixed iOS and silently cost Android its install
 * prompt — Chrome requires it (measured: `start-url-not-valid` from
 * `Page.getInstallabilityErrors`; see `scripts/check-installability.mjs`).
 *
 * A manifest whose `start_url` IS the current page satisfies both. The full
 * reasoning, and why the href must be a `data:` URL rather than a `blob:` one,
 * is in `lib/page-manifest.ts`.
 *
 * It runs on EVERY route, not just arche routes: adding from anywhere should
 * pin what you were looking at, and one code path is easier to reason about
 * than a rule about which pages are special.
 *
 * Failure is silent BY DESIGN. If the link tag is missing the helper returns
 * false and nothing happens — the static `public/manifest.webmanifest` remains.
 *
 * That static file is not a redundant copy: it is what SAFARI reads. Safari
 * fetches the manifest once during page load and never re-reads it, so this
 * rewrite is invisible to it — which is why the static file omits `start_url`,
 * leaving Safari to fall back to the document URL. Chrome does re-read, and
 * takes its `start_url` from what this installs. The two are kept apart
 * deliberately; see `tests/architecture/installable-pwa.test.ts`.
 */

// ---------------------------------------------------------------------------
// Who the current page is
// ---------------------------------------------------------------------------

/**
 * A page deeper in the tree (an arche) knows its own name and icon; the tag it
 * has to be written into lives up here. That is one value with two owners at
 * different depths, and the usual answers both cost more than they are worth:
 * a context provider around the router would re-render the whole app on every
 * navigation, and a second effect writing the same tag would race this one to
 * decide which write lands last.
 *
 * So there is exactly ONE writer — this component — subscribed to a store the
 * hook below publishes into. Nothing else may touch the manifest or
 * apple-touch-icon tags.
 */
let identity: PageManifestIdentity | null = null;
const listeners = new Set<() => void>();

function publishIdentity(next: PageManifestIdentity | null): void {
  identity = next;
  for (const listener of listeners) listener();
}

function subscribeIdentity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable between publishes, which is what useSyncExternalStore requires. */
function readIdentity(): PageManifestIdentity | null {
  return identity;
}

/**
 * Declare what this page is, for as long as it is mounted.
 *
 * Pass null (or a nameless arche) to leave the page as plain Fias. On unmount
 * the identity is withdrawn, so navigating away restores the platform's own
 * name and icons without the leaving page having to remember to.
 *
 * `icons` is compared by its srcs rather than by array identity — the metadata
 * query hands back a fresh array on every render.
 */
export function usePageManifestIdentity(
  name: string | undefined,
  icons?: PageManifestIcon[],
): void {
  // The key is only for change DETECTION; the effect publishes the real array.
  const iconKey = (icons ?? []).map((i) => `${i.sizes}:${i.src}`).join('|');
  const latestIcons = useRef(icons);
  latestIcons.current = icons;

  useEffect(() => {
    if (!name) return;
    publishIdentity({ name, icons: latestIcons.current ?? [] });
    return () => publishIdentity(null);
  }, [name, iconKey]);
}

// ---------------------------------------------------------------------------

export function PageManifestLink(): null {
  const location = useLocation();
  const current = useSyncExternalStore(
    subscribeIdentity,
    readIdentity,
    useCallback(() => null, []),
  );

  useEffect(() => {
    applyPageManifest(document, window.location.origin, location.pathname, current);
    // iOS reads the home-screen icon from this tag, not from the manifest.
    applyAppleTouchIcon(document, current);
  }, [location.pathname, current]);

  return null;
}
